import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';
import { useUser } from '../../Hooks/useUser';
import axios from '../../api/axios';
import { useFormData } from "../../Hooks/useFormDataContext";

const endpoint = '/api/users/alluserinfo';

const Insurance = (props) => {
  const { user: contextUser } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { updateForm } = useFormData();

  const isControlled = Object.prototype.hasOwnProperty.call(props, 'User');

  const [User, setUser] = useState(props.User ?? {});
  const [UserSettings, setUserSettings] = useState(props.UserSettings ?? {});
  const [saving, setSaving] = useState(false);

  // Admin mode: mirror whatever the parent fetched — no competing request here.
  useEffect(() => {
    if (!isControlled || !props.User) return;
    setUser(props.User);
  }, [isControlled, props.User]);

  useEffect(() => {
    if (!isControlled || !props.UserSettings) return;
    setUserSettings(props.UserSettings);
  }, [isControlled, props.UserSettings]);

  // Profile mode only: self-fetch based on the logged-in user.
  useEffect(() => {
    if (isControlled) return;
    if (!contextUser?.UserID) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        const result = await axios.get(`${endpoint}/${contextUser.UserID}`);
        if (cancelled || !result) return;
        setUser(result.data.user);
        setUserSettings(result.data.userSettings);
      } catch (error) {
        if (!cancelled) {
          console.error('Insurance fetch failed:', error);
          enqueueSnackbar('Failed to load insurance information', { variant: 'error' });
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isControlled, contextUser?.UserID, enqueueSnackbar]);

  function handleFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setUserSettings(prev => ({ ...prev, [name]: value }));
    if (props.UseFormContext) {
      updateForm("UserSettings", name, value);
    }
  }

  function handleCheckboxChange(input) {
    const target = input.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.id;
    setUserSettings(prev => ({ ...prev, [name]: value }));
    if (props.UseFormContext) {
      updateForm("UserSettings", name, value ? true : false);
    }
  }

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(endpoint, { user: User, userSettings: UserSettings });
      enqueueSnackbar("Insurance Section Saved!");
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Insurance Section Save Failed: " + (ex.response?.data?.error || ex.message), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div id="tab7">
        <div className="row px-5 pt-5">
          <div className="col-md-12">
            <p><strong>Health Insurance</strong><br />
              LIABILITY RELEASE: As a volunteer on this project, I understand that there are certain risks and dangers that are inherent while traveling and participating in such a project. Some of those dangers include accidents while traveling, accidents while working (falls, electrical shock, cuts and other injuries from power equipment, etc.), sickness and other accidents or injuries, foreseeable and unforeseeable, that might pose a risk to me of permanent injury or death. If I accept a term of volunteer service, I wish to make it clear that my understanding is that all sponsoring Baptist Conventions and bodies do not assume any responsibility for loss of property, damage to the same, personal harm or injury or illness that may come, and I, for myself, my heirs, executors, administrators, distributes and assigns, do hereby absolve all sponsoring Baptist bodies and hold them harmless from any claim or demand which I or my agents or heirs might conceivably assert upon the Baptist bodies. </p>
          </div>
          <div className="col-md-4">
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input type="checkbox" id="BP_I_Health" name="BP_I_Health" checked={!!UserSettings.BP_I_Health} onChange={handleCheckboxChange} />
                <label htmlFor="BP_I_Health">Do you have Health Insurance?</label>
              </div>
            </div>
          </div>
          <div className="col-md-8"> </div>
          <div className="col-md-12 mb-3">
            <p><strong>Supplemental Insurance</strong><br />
              Volunteer may be automatically enrolled in a supplemental insurance policy by North Carolina Baptist Men's office. </p>
          </div>
          <div className="col-md-12">
            <div className="legal form-group form-check">
              <input type="checkbox" className="form-check-input" name="BP_I_Agree" id="BP_I_Agree" checked={!!UserSettings.BP_I_Agree} onChange={handleCheckboxChange} />
              <label className="form-check-label" htmlFor="BP_I_Agree">I understand that the supplemental accident insurance that will be provided for me by NC Baptist Men is supplemental and secondary insurance. I am responsible to have and provide my own primary health insurance coverage. I accept full responsibility for any medical bills and expenses that are not covered by this secondary coverage. It is not the responsibility for NC Baptist Men to provide me with health insurance coverage for this mission trip and I will not hold them responsible for any medical expenses that result from sickness, accident or injury while participating on this trip.: </label>
            </div>
          </div>
        </div>
        <hr />
        <div className="row px-5 pt-5">
          <div className="col-md-4">
            <div className="form-group">
              <label className="required-caption">Beneficiary First Name<span className="required-field">*</span></label>
              <input type="text" maxLength="200" id="BP_I_FName" name="BP_I_FName" className="form-control" placeholder="Beneficiary First Name" value={UserSettings.BP_I_FName || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="required-caption">Beneficiary Middle Name</label>
              <input type="text" maxLength="200" id="BP_I_MName" name="BP_I_MName" className="form-control" placeholder="Beneficiary Middle Name" value={UserSettings.BP_I_MName || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="required-caption">Beneficiary Last Name<span className="required-field">*</span></label>
              <input type="text" maxLength="200" id="BP_I_LName" name="BP_I_LName" className="form-control" placeholder="Beneficiary Last Name" value={UserSettings.BP_I_LName || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="required-caption">Beneficiary Relationship<span className="required-field">*</span></label>
              <input type="text" maxLength="200" id="BP_I_Relation" name="BP_I_Relation" className="form-control" placeholder="Beneficiary Relationship" value={UserSettings.BP_I_Relation || ''} onChange={handleFieldChange} />
            </div>
          </div>
        </div>
        <hr />
        {!props.UseFormContext ?
          <div className="row my-5">
            <div className="col-md-12 text-center">
              <a className="button-default button-green" type='submit' onClick={handleSubmit} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px', minWidth: '120px', padding: '8px 20px', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                {saving && <CircularProgress size={14} style={{ color: 'white' }} />}
                Save
              </a>
            </div>
          </div> : ""}
      </div>
    </form>
  );
};
export default Insurance;